import React, { forwardRef, Ref, useMemo, useState, useEffect } from 'react';
import { Link } from 'react-style-guide';
import {
  Thumbnail2d,
  ThumbnailTypes,
  ThumbnailFormat,
  ThumbnailGameIconSize
} from 'roblox-thumbnails';
import { PlayButton } from 'Roblox';
import { queryParams } from '../constants/browserConstants';
import { buildGameDetailUrl, getSession } from '../utils/browserUtils';
import { getInGameFriends } from '../utils/parsingUtils';
import useFriendsPresence from '../hooks/useFriendsPresence';
import { TSharedGameTileProps, GameTileStats, GameTileFriendsInGame } from './GameTileUtils';
import { TGetGameDetails } from '../types/bedev1Types';
import bedev1Services from '../services/bedev1Services';
import '../../../../css/common/_gameTiles.scss';

const { PlayabilityStatuses } = PlayButton;
const LoadingPlayButton = () => <div className='shimmer play-button game-card-thumb-container' />;
const PurchasePlayButton = () => <span className='icon-common-play' />;
const ErrorPlayButton = (status: keyof typeof PlayabilityStatuses) => {
  switch (status) {
    case PlayabilityStatuses.UniverseRootPlaceIsPrivate:
      return (
        <div className='btn-growth-lg play-button'>
          <span className='icon-status-private' />
        </div>
      );
    default:
      return (
        <div className='btn-growth-lg play-button'>
          <span className='icon-status-unavailable' />
        </div>
      );
  }
};

export const FeaturedGridTile = forwardRef(
  (
    { id, onTileClick, gameData, translate }: TSharedGameTileProps,
    ref: Ref<HTMLDivElement>
  ): JSX.Element => {
    const [game, setGame] = useState<TGetGameDetails | undefined>();
    const friendsData = useFriendsPresence();
    const session = getSession(queryParams.gameSearchQuerySession);
    const friendsDataInGame = useMemo(() => getInGameFriends(friendsData, gameData.universeId), [
      friendsData,
      gameData.universeId
    ]);

    const cardDescription = useMemo(() => {
      if (friendsDataInGame.length > 0 && game) {
        return (
          <GameTileFriendsInGame
            gameData={game}
            friendData={friendsDataInGame}
            translate={translate}
          />
        );
      }
      return <div className='game-card-description-info font-body'>{game?.description}</div>;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [friendsDataInGame]);

    useEffect(() => {
      const fetchGameDetails = async () => {
        try {
          const response = await bedev1Services.getGameDetails(gameData.placeId);
          setGame(response);
        } catch (e) {
          console.error(e);
        }
      };
      // eslint-disable-next-line no-void
      void fetchGameDetails();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div ref={ref} className='featured-grid-item-container game-card-container'>
        <Link
          onClick={() => {
            onTileClick(gameData, id);
          }}
          url={buildGameDetailUrl(gameData.placeId, gameData.name, {
            [queryParams.gameSearchQuerySession]: session
          })}
          className='game-card-link'
          id={gameData.universeId.toString()}>
          <Thumbnail2d
            type={ThumbnailTypes.gameIcon}
            size={ThumbnailGameIconSize.size512}
            targetId={gameData.universeId}
            containerClass='game-card-thumb-container'
            format={ThumbnailFormat.jpeg}
            altName={gameData.name}
          />
          <div className='game-card-name-info'>
            <div>
              <div className='game-card-name game-name-title' title={gameData.name}>
                {gameData.name}
              </div>
              <GameTileStats
                totalUpVotes={gameData.totalUpVotes}
                totalDownVotes={gameData.totalDownVotes}
                playerCount={gameData.playerCount}
              />
            </div>
            <PlayButton
              universeId={gameData.universeId}
              placeId={gameData.placeId}
              loadingComponent={LoadingPlayButton}
              purchaseComponent={PurchasePlayButton}
              errorComponent={ErrorPlayButton}
            />
          </div>
          {cardDescription}
        </Link>
      </div>
    );
  }
);

export default FeaturedGridTile;
